import { GoogleMap, useLoadScript } from "@react-google-maps/api";
import { useEffect, useRef, useState } from "react";

import { Button } from "@mantine/core";
import { IconAspectRatio } from "@tabler/icons-react";
import "./style.scss";
import { getStopSequence } from "../trips/helper";

import {
  CourierResponse,
  HasLatLong,
  TripResponse,
  TripStopLabelStruct,
  TripStopResponse,
  VehicleStopResponse,
  VehicleTripResponse,
} from "types/trip.types";
import { WarehouseResponse } from "types/responses.type";
import { ColorPalette } from "types/ColorSpectrum";
import { useSelector } from "react-redux";
import { RootState } from "store";
import TripPath from "./TripPath";
import EventRegistry, { EventHandler } from "utils/eventRegistry";
import UnassignedStops from "./UnassignedStops";

// import { stopSortBySchedulerTimes, stopSortByComputedTimes } from '../../utils/trips.helper';

const MAX_MAP_ZOOM = 17;
interface MapProps {
  vehicleTrips?: VehicleTripResponse[];
  currentTripId?: number;
  warehouses?: WarehouseResponse[];
  defaultCenter?: HasLatLong;
  visibleTripsIds?: number[];
  highlightStopId?: number;
  boundPoints?: HasLatLong[];
  isLoading?: boolean;
  unassignedStops?: TripStopResponse[];
  additionalMarkers?: { position: HasLatLong; label: string }[];
  focusedPoint?: HasLatLong;
  zoom?: number;
  /* eslint-disable */
  eventRegistry?: EventRegistry;
  onStopHover?: (stopId: number, isOver: boolean) => void;
  onCustomerHover?: (customerId: number, isOver: boolean) => void;
  onCustomerClick?: (customerId: number) => void;
  onMapClick?: () => void;
  onStopClick?: (stopId: number) => void;
  onCourierClick?: (courierId: number) => void;
  onAction?: (action: string, params?: any) => void;
  /* eslint-enable */
}

const Map = ({
  vehicleTrips,
  currentTripId,
  warehouses,
  visibleTripsIds,
  highlightStopId,
  boundPoints,
  isLoading,
  focusedPoint,
  unassignedStops,
  zoom,
  eventRegistry,
  onMapClick,
  onStopClick,
  onCourierClick,
  onCustomerClick,
  onAction,
}: MapProps) => {
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GEOCODING_API_KEY ?? "",
    mapIds: ["6c5881e4d125cebc5d139889"],
  });
  // const currentTrip = vehicleTrips?.find(
  //   (t) => t.vehicleTripId == currentTripId
  // );
  const currentTrip = useSelector((state: RootState) =>
    state.trips.trips?.find((t) => t.vehicleTripId == currentTripId),
  );
  // styleID:5c9b16f0e7d4928e9c2cb3cb
  //   const google: any = window.google;

  const mapRef = useRef<any>(null);
  const [infoHash, setInfoHash] = useState<string>();
  const [zoomLevel, setZoomLevel] = useState("all");
  const [lockVieport, setLockVieport] = useState(false);

  const onMapLoad = (gmap: any) => {
    mapRef.current = gmap;
  };

  const reBound = (forcedZoomLevel?: string, force?: boolean): void => {
    // if (lockVieport && !force) return;
    try {
      const bounds = new window.google.maps.LatLngBounds();
      const extendBounds = (bPoint: any, obj?: any) => {
        if (bPoint.lat && bPoint.lng)
          bounds.extend({ lat: +bPoint.lat, lng: +bPoint.lng });
        else console.error("Invalid LatLon", { bPoint, obj });
      };
      if (!forcedZoomLevel && boundPoints && boundPoints.length) {
        if (boundPoints.length === 1) {
          setZoomLevel("location");
        } else {
          setZoomLevel("collection");
        }
        boundPoints.forEach((bPoint) => {
          extendBounds(bPoint);
        });
      } else if (vehicleTrips?.length) {
        // console.log(`zoom to trip ${selectedTripIndex}`);
        if (currentTrip) {
          setZoomLevel("collection");
          if (currentTrip?.vehicleStops?.length) {
            currentTrip.vehicleStops?.forEach((stop) => {
              extendBounds({ lat: stop.latitude, lng: stop.longitude }, stop);
            });
          }
        } else if (currentTripId === -1) {
          const unassignedTrip = vehicleTrips.find(
            (t) => t.vehicleTripId === -1,
          );
          if (unassignedTrip?.trips?.length) {
            unassignedTrip.trips?.forEach((trip) => {
              trip.tripStops?.forEach((stop) => {
                if (stop.place) {
                  extendBounds(
                    { lat: stop.place.latitude, lng: stop.place.longitude },
                    stop,
                  );
                }
              });
            });
            setZoomLevel("collection");
          }
        } else {
          setZoomLevel("all");
          let anyVisibleTrips = false;
          if (mapRef.current) {
            if (vehicleTrips && vehicleTrips.length) {
              vehicleTrips.forEach((vehicleTrip) => {
                const tripIsVisible =
                  !visibleTripsIds ||
                  visibleTripsIds.includes(vehicleTrip.vehicleTripId ?? 0);
                anyVisibleTrips ||= tripIsVisible;
                tripIsVisible &&
                  (vehicleTrip.vehicleStops ?? []).forEach((stop) => {
                    extendBounds(
                      {
                        lat: stop.latitude,
                        lng: stop.longitude,
                      },
                      stop,
                    );
                  });
              });
            }
            // if (customersLocations && customersLocations.length) {
            //   customersLocations.forEach((location) => {
            //     extendBounds(
            //       { lat: location?.latitude, lng: location.longitude },
            //       { customerLocation: location }
            //     );
            //   });
            //   anyVisibleTrips = true;
            // }
            if (!anyVisibleTrips && warehouses && warehouses.length) {
              warehouses.forEach((warehouse) => {
                extendBounds(
                  {
                    lat: warehouse.place?.latitude,
                    lng: warehouse.place?.longitude,
                  },
                  { warehouse },
                );
              });
            }
          }
        }
      } else {
        focusOn({ latitude: 38.7, longitude: -100.4 }, 4);
      }

      mapRef.current?.fitBounds(bounds);
      if (mapRef.current?.getZoom() > MAX_MAP_ZOOM) {
        mapRef.current?.setZoom(MAX_MAP_ZOOM);
      }
      // alert(mapRef?.getZoom());
    } catch (err) {
      console.error(err);
    }
  };
  const centerTo = (center: HasLatLong) => {
    if (mapRef.current) {
      mapRef.current?.panTo(center);
      window.google.maps.event.addListenerOnce(
        mapRef.current,
        "idle",
        function () {
          if (center) {
            mapRef.current?.setCenter(center);
          }
        },
      );
    }
  };
  const focusOn = (point: HasLatLong, zoom = 18) => {
    const center = { lat: point.latitude, lng: point.longitude };
    if (mapRef.current) {
      mapRef.current?.panTo(center);
      window.google.maps.event.addListenerOnce(
        mapRef.current,
        "idle",
        function () {
          if (center) {
            mapRef.current?.setCenter(center);
            mapRef.current?.setZoom(zoom);
          }
        },
      );
    }
  };
  const handleBoundChange = () => {
    setLockVieport(true);
  };
  const handleReboundBtnClicked = () => {
    let zLevel = "all";
    if (zoomLevel === "location") {
      zLevel = "collection";
    }
    setLockVieport(false);
    reBound(zLevel, true);
  };

  // const getPathLocation = (locationQueue: StopLocation[]) => {
  //   if (locationQueue.length > 1) {
  //     const thisLocation = locationQueue[1] as StopLocation;
  //     const prevLocation = locationQueue.shift() as StopLocation;
  //     return [prevLocation, thisLocation];
  //   }
  //   return [] as StopLocation[];
  // };
  useEffect(() => {
    reBound(undefined, true);
  }, [mapRef, boundPoints, currentTripId, vehicleTrips]);
  useEffect(() => {
    if (isLoading) return;
    // let highlightstopIndex = 0;
    // const tripIndex = vehicleTrips?.findIndex((trip) => {
    //   return (
    //     trip.tripStops?.length &&
    //     trip.tripStops.findIndex((stop, stopIndex) => {
    //       const found = highlightStopId === stop.tripStopId;
    //       if (found) highlightstopIndex = stopIndex;
    //       return found;
    //     }) >= 0
    //   );
    // });
    // if (highlightstopIndex && trips) {
    //   const location =
    //     trips[tripIndex ?? 0].tripStops[highlightstopIndex].place;
    //   console.log({ highlightstopIndex, location });

    //   if (location)
    //     centerTo({
    //       latitude: location.latitude,
    //       longitude: location.longitude,
    //     });
    // }
  }, [highlightStopId, isLoading]);
  useEffect(() => {
    if (focusedPoint) focusOn(focusedPoint, zoom);
  }, [focusedPoint]);
  useEffect(() => {
    if (isLoading) return;
    const newHash = "T:" + visibleTripsIds?.sort().join("|");
    // +
    // "C" +
    // (customersLocations?.map((loc) => loc?.locationId ?? 0) ?? [])
    //   .sort()
    //   .join("|");
    if (newHash !== infoHash) {
      reBound("all");
      setInfoHash(newHash);
    }
    // }, [visibleTripsIds, customersLocations, isLoading]);
  }, [visibleTripsIds, isLoading]);
  // useEffect(() => {
  //   if (trips?.length) {
  // tripsRef.current = trips;
  //   }
  // }, [trips]);

  useEffect(() => {
    if (eventRegistry) {
      eventRegistry?.registerEvent(
        "TRIPS.MAP",
        "focusOnVehicleStop",
        (params: { point: HasLatLong; zoom?: number }) => {
          focusOn(params.point, params.zoom);
        },
      );
    }
  }, [eventRegistry]);

  let anyVisibleTrips = false;

  return !isLoaded ? (
    <h1>Loading...</h1>
  ) : (
    <GoogleMap
      options={{
        streetViewControl: false,
        zoomControl: true,
        mapId: "6c5881e4d125cebc5d139889",
        // mapTypeId: "5c9b16f0e7d4928e9c2cb3cb",
        disableDefaultUI: true,
        colorScheme: "DARK",
      }}
      mapContainerClassName="map-area-google-map"
      onLoad={onMapLoad}
      onClick={onMapClick}
      onBoundsChanged={handleBoundChange}
      // onZoomChanged={() => {
      //   setStopRebound(true);
      // }}
    >
      <UnassignedStops stops={unassignedStops ?? []} onAction={onAction} />
      {/* TripPath component for currentTrip */}
      {currentTrip ? (
        <TripPath
          vehicleTrip={currentTrip}
          colorIndex={currentTrip.additionalData?.colorIndex}
          onAction={onAction}
        />
      ) : (
        vehicleTrips?.map((t) => (
          <TripPath
            vehicleTrip={t}
            colorIndex={t.additionalData?.colorIndex}
            onAction={onAction}
          />
        ))
      )}
      <div className="map-rebound-btn">
        <Button
          variant="default"
          radius={0}
          style={{ padding: 6, minWidth: 0 }}
          onClick={() => {
            handleReboundBtnClicked();
          }}
        >
          <IconAspectRatio />
        </Button>
      </div>
    </GoogleMap>
  );
};

export default Map;
